import { useSessionContext } from '@/contexts/session/context';
import { genId, safeJson } from '@/scripts/utils';
import type { RetryOptions } from '@/scripts/utils/queue';
import { retry, useQueue } from '@/scripts/utils/queue';
import { useRefs } from '@/scripts/hooks';
import { useAudio } from '@/scripts/hooks/useAudio';
import { chatApis } from '@/configs/apis/chat';
import { defaultMsgExpireDuration } from '@/configs/options';
import type {
  AddMessageParams,
  ChatMessage,
  ChatMessageId,
  ChatMessageIdParams,
  ChatMessageMessageId,
  ChatMessageMessageIdParams,
  ChatMessagePartial,
  ChatMessagesArgMessageIdParams,
  ChatMessagesLoadParams,
  DBChatMessagesArgPartial,
  RetryParams,
  SendParams,
} from '../hooks/useChatMessages/types';
import {
  ChatMessageCategory,
  ChatMessageOwnerId,
  ChatMessagePayloadType,
  ChatMessagePulled,
  ChatMessageStatus,
} from '../hooks/useChatMessages/types';
import type {
  Chat,
  ChatParams,
  ChatChatId,
  ChatArgType,
  SendChatParams,
  ChatSystemChats,
  ChatQuery,
} from '../hooks/useChats/types';
import { ChatCategory, ChatSystemChatsValues } from '../hooks/useChats/types';
import type { ChatSocketFlags, ChatSocketRes } from '../hooks/useChatSocket/types';
import { ChatSocketControls, FlagValue } from '../hooks/useChatSocket/types';
import { serverTime } from '../hooks/useSystemTime/utils';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import {
  filterMessagesArgs,
  mapMessagesArgs,
  parseMessageId,
  pushMessagesArgs,
} from '../hooks/useChatMessages/utils';
import type { chat } from '../protobuf/protobufs/chat';
import { isFileMessage } from '../hooks/useFileMessages/utils';
import { FileInfoStatus, type FileInfo } from '../hooks/useFileMessages/types';
import type { UseChatsValueResult } from './chats';

export interface SendMessageToServerParams {
  sendParams: SendParams;
  message: ChatMessage;
  handlers?: {
    encrypt?: (message: ChatMessage) => ResolveValue<ChatMessage>;
    updateMessageBefore?: (
      message: ChatMessageIdParams,
      res: ChatSocketRes,
      params: SendParams
    ) => ResolveValue<ChatMessageIdParams>;
  };
  flags?: ChatSocketFlags;
  autoRetry?: boolean;
}

export type ChatMessagesArgMessageIdPullParams = ChatMessagesArgMessageIdParams & {
  isNew?: boolean;
};

export type UseChatMessagesValueResult = ReturnType<typeof useChatMessagesValue>;

export function useChatMessagesValue({ hooks }: ChatContextHooksProps) {
  const { chatSocket, chats, chatMessages, chatMediaCall } = hooks;
  const {
    session: { user },
  } = useSessionContext();

  const { play: playReceiveSound } = useAudio({ src: '/media/receive.mp3' });

  const { send } = chatSocket;

  const {
    chatsState: { activatedChatId },
  } = chats;

  const {
    messagesState: { getMessages: getStateMessages },
  } = chatMessages;

  const activatedMessages = useMemo(
    () => (activatedChatId ? getStateMessages(activatedChatId) : undefined),
    [activatedChatId, getStateMessages]
  );

  const refs = useRefs({ activatedChatId });

  const sendMessage = useCallback(
    async (
      sendChatParams: SendChatParams,
      params: chat.ISendMessageRequest,
      flags?: ChatSocketFlags,
      retryOptions?: RetryOptions
    ) => {
      return send(
        {
          headers: {
            control: ChatSocketControls.SEND,
            flags,
          },
          body: {
            chatId: sendChatParams.chat_id,
            receiverId: sendChatParams.receiver_id,
            ...params,
          },
        },
        retryOptions
      );
    },
    [send]
  );

  const pullMessageIds = useCallback(
    (type: ChatCategory, serverSequence: number, retryOptions?: RetryOptions) => {
      return chatApis
        .getSequence(type, serverSequence)
        .then((sequence) => {
          return send(
            {
              headers: {
                control: ChatSocketControls.PULL_IDS,
              },
              body: {
                sequence,
                msgType: type,
              },
            },
            retryOptions
          )
            .then((res) => {
              const map: Record<
                ChatChatId,
                Record<ChatMessageMessageId, ChatMessageMessageIdParams>
              > = {};

              const list: any[] = res.body.messageSequenceList;

              list.forEach((item: any) => {
                const isSelf = item.senderId === user?.userId;
                const messagesMap = map[item.chatId] || (map[item.chatId] = {});
                const message = parseMessageId(item.messageId);
                messagesMap[item.messageId] = {
                  ...item,
                  ...message,
                  create_at: item.createdAt,
                  update_at: item.createdAt,
                  owner_id: item.senderId,
                  category: isSelf
                    ? ChatMessageCategory.SEND
                    : ChatMessageCategory.RECEIVE,
                };
              });

              return {
                res,
                map,
              };
            })
            .then(({ res, map }) => {
              const addList: ChatMessagesArgMessageIdParams[] = [];
              const addListSender: ChatMessagesArgMessageIdParams[] = [];
              const addListMedia: ChatMessagesArgMessageIdParams[] = [];
              const updateList: ChatMessagesArgMessageIdParams[] = [];
              const updateListMedia: ChatMessagesArgMessageIdParams[] = [];
              const systemList: ChatMessagesArgMessageIdParams[] = [];
              const systemEncryptionList: ChatMessagesArgMessageIdParams[] = [];

              for (const chat_id in map) {
                const addMessagesParams: ChatMessageMessageIdParams[] = [];
                const addMessagesSenderParams: ChatMessageMessageIdParams[] = [];
                const addMessagesMediaParams: ChatMessageMessageIdParams[] = [];
                const updateMessagesParams: ChatMessageMessageIdParams[] = [];
                const updateMessagesMediaParams: ChatMessageMessageIdParams[] = [];
                const systemParams: ChatMessageMessageIdParams[] = [];
                const systemEncryptionParams: ChatMessageMessageIdParams[] = [];

                const messages = Object.values(map[chat_id]);

                const item: ChatMessagesArgMessageIdParams = {
                  chat_id,
                  messages,
                };
                messages.forEach((message) => {
                  const isStatus = [
                    ChatMessageStatus.READ,
                    ChatMessageStatus.UNREAD,
                  ].includes(message.status!);

                  const isSystemGroupNotice =
                    message.owner_id === ChatMessageOwnerId.SYSTEM_GROUP_NOTICE;

                  const isMediaCall = chatMediaCall.isMediaPayloadTypeCall(
                    message.payload_type!
                  );

                  const isStatusMedia = chatMediaCall.isMediaPayloadTypeStatus(
                    message.payload_type!
                  );
                  // Only single media
                  const isStatusSingleMedia = isStatusMedia && !isSystemGroupNotice;

                  if (
                    [
                      ChatMessagePayloadType.GROUP_ENCRYPTION_COMMIT,
                      ChatMessagePayloadType.GROUP_ENCRYPTION_WELCOME,
                      ChatMessagePayloadType.GROUP_ENCRYPTION_LEAVE,
                    ].includes(message.payload_type as ChatMessagePayloadType)
                  ) {
                    systemEncryptionParams.push(message);
                  } else if (
                    ChatSystemChatsValues.includes(message.chatId as ChatSystemChats)
                  ) {
                    systemParams.push(message);
                  } else if (isStatus || isStatusSingleMedia) {
                    if (isStatusSingleMedia) {
                      const message_: ChatMessageMessageIdParams = {
                        message_id: message.message_id,
                        status: message.payload_type as unknown as ChatMessageStatus,
                        owner_id: message.owner_id,
                        update_at: message.update_at,
                      };
                      updateMessagesParams.push(message_);
                      updateMessagesMediaParams.push(
                        message.cli_msg_id
                          ? {
                              ...message_,
                              cli_msg_id: message.cli_msg_id,
                            }
                          : message_
                      );
                    } else {
                      updateMessagesParams.push({
                        message_id: message.message_id,
                        status: message.status,
                        update_at: message.update_at,
                      });
                    }
                  } else {
                    if (isSystemGroupNotice && (isMediaCall || isStatusMedia)) {
                      systemParams.push(message);
                    } else {
                      addMessagesParams.push(message);
                      const isSender = message.category === ChatMessageCategory.SEND;
                      if (isSender) {
                        addMessagesSenderParams.push(message);
                      }
                      if (isMediaCall) {
                        addMessagesMediaParams.push(message);
                      }
                    }
                  }
                });

                if (addMessagesParams.length) {
                  addList.push({
                    ...item,
                    messages: addMessagesParams,
                  });
                }

                if (addMessagesSenderParams.length) {
                  addListSender.push({
                    ...item,
                    messages: addMessagesSenderParams,
                  });
                }

                if (addMessagesMediaParams.length) {
                  addListMedia.push({
                    ...item,
                    messages: addMessagesMediaParams,
                  });
                }

                if (updateMessagesParams.length) {
                  updateList.push({
                    ...item,
                    messages: updateMessagesParams,
                  });
                }

                if (updateMessagesMediaParams.length) {
                  updateListMedia.push({
                    ...item,
                    messages: updateMessagesMediaParams,
                  });
                }

                if (systemEncryptionParams.length) {
                  systemEncryptionList.push({
                    ...item,
                    messages: systemEncryptionParams,
                  });
                }

                if (systemParams.length) {
                  systemList.push({
                    ...item,
                    messages: systemParams,
                  });
                }
              }

              return {
                res,
                addList,
                addListSender,
                addListMedia,
                updateList,
                updateListMedia,
                systemList,
                systemEncryptionList,
                map,
              };
            })
            .then(({ res, ...lists }) => {
              const sequence: number | undefined = res.body.maxSequence || undefined;
              return Object.assign(res, {
                ...lists,
                sequence,
                async saveSequence() {
                  if (sequence !== undefined) {
                    await chatApis.setSequence(type, sequence);
                  }
                },
              });
            });
        })
        .catch((err) => {
          if (err?.code === 'SEQUENCE_IS_OLD') {
            return null;
          }
          throw err;
        });
    },
    [chatMediaCall, send, user?.userId]
  );

  const pullMessages = useCallback<
    <T extends ChatMessagesArgMessageIdParams>(
      type: ChatCategory,
      paramsList: T[],
      handlers?: {
        pulled?: (
          pulledMessage: ChatMessageMessageIdParams,
          params: T
        ) => ResolveValue<ChatMessagePartial>;
      },
      retryOptions?: RetryOptions
    ) => Promise<{
      list: ChatMessagesArgMessageIdParams[];
      pulledList: ChatMessagesArgMessageIdParams[];
      pulledListSender: ChatMessagesArgMessageIdParams[];
      pulledListMediaSender: ChatMessagesArgMessageIdParams[];
      pulledListMediaReceiver: ChatMessagesArgMessageIdParams[];
      deletedList: ChatMessagesArgMessageIdParams[];
    }>
  >(
    async (type, paramsList, handlers, retryOptions) => {
      const { pulled } = handlers || {};

      const pulledMessageMap: Record<
        ChatMessageMessageId,
        {
          params: any;
          message: ChatMessageMessageIdParams;
        }
      > = {};

      let list = paramsList.map((params) => {
        const { messages } = params;
        const messages_: ChatMessageMessageIdParams[] = [];

        messages.forEach((message) => {
          if (message.pulled !== ChatMessagePulled.YES) {
            message = {
              ...message,
            };
            pulledMessageMap[message.message_id] = {
              params,
              message,
            };
          }
          messages_.push(message);
        });

        return {
          ...params,
          messages: messages_,
        };
      });

      const ids: ChatMessageMessageId[] = Object.keys(pulledMessageMap);

      const pulledListArgs = pushMessagesArgs<ChatMessagesArgMessageIdParams>();
      const pulledListSenderArgs = pushMessagesArgs<ChatMessagesArgMessageIdParams>();
      const pulledListMediaSenderArgs =
        pushMessagesArgs<ChatMessagesArgMessageIdParams>();
      const pulledListMediaReceiverArgs =
        pushMessagesArgs<ChatMessagesArgMessageIdParams>();
      const deletedListArgs = pushMessagesArgs<ChatMessagesArgMessageIdParams>();

      if (ids.length) {
        await send(
          {
            headers: {
              control: ChatSocketControls.PULL_MSG,
            },
            body: {
              messageIdList: ids,
              msgType: type,
            },
          },
          retryOptions
        ).then(async (res) => {
          const infoList: any[] = res.body.messageInfoList;
          await Promise.all(
            infoList.map(async (item) => {
              const pulledMessage = pulledMessageMap[item.messageId];
              if (pulledMessage) {
                const senderId =
                  (item.senderId === ChatMessageOwnerId.SYSTEM_GROUP_NOTICE &&
                    safeJson(item.payload, { user_id: '' }).user_id) ||
                  item.senderId ||
                  '';

                const isSelf = senderId === user?.userId;

                Object.assign(pulledMessage.message, {
                  ...item,
                  owner_id: senderId,
                  expire_duration: item.expireDuration || defaultMsgExpireDuration,
                  payload: item.payload,
                  encrypt_index: item.encryptIndex,
                  pulled: ChatMessagePulled.YES,
                  category: isSelf
                    ? ChatMessageCategory.SEND
                    : ChatMessageCategory.RECEIVE,
                  status: isSelf ? ChatMessageStatus.SUCCESS : ChatMessageStatus.UNREAD,
                } as ChatMessagePartial);

                const result = await pulled?.(
                  pulledMessage.message,
                  pulledMessage.params
                );

                Object.assign(pulledMessage.message, result);

                pulledListArgs.push(
                  pulledMessage.message,
                  pulledMessage.params.chat_id
                );

                if (isSelf) {
                  pulledListSenderArgs.push(
                    pulledMessage.message,
                    pulledMessage.params.chat_id
                  );
                }

                if (
                  chatMediaCall.isMediaPayloadTypeCall(
                    pulledMessage.message.payloadType! // Use server type
                  )
                ) {
                  if (isSelf) {
                    pulledListMediaSenderArgs.push(
                      pulledMessage.message,
                      pulledMessage.params.chat_id
                    );
                  } else {
                    pulledListMediaReceiverArgs.push(
                      pulledMessage.message,
                      pulledMessage.params.chat_id
                    );
                  }
                }
              }
            })
          );
        });
      }

      list = await filterMessagesArgs(list, (msg, chat_id) => {
        if (msg.pulled === ChatMessagePulled.YES) {
          return true;
        }
        deletedListArgs.push(msg, chat_id);
      });

      return {
        list: ids.length ? list : paramsList,
        pulledList: pulledListArgs.args,
        pulledListSender: pulledListSenderArgs.args,
        pulledListMediaSender: pulledListMediaSenderArgs.args,
        pulledListMediaReceiver: pulledListMediaReceiverArgs.args,
        deletedList: deletedListArgs.args,
      };
    },
    [chatMediaCall, send, user?.userId]
  );

  const messageArrivalReply = useCallback(
    async (params: chat.IMessageArrivalReplyRequest, retryOptions?: RetryOptions) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.MSG_ARRIVAL,
          },
          body: params,
        },
        retryOptions
      );
      return res.body.code;
    },
    [send]
  );

  const messageReadReply = useCallback(
    async (params: chat.IReadBurnMessageReplyRequest, retryOptions?: RetryOptions) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.MSG_READ,
          },
          body: params,
        },
        retryOptions
      );
      return res.body;
    },
    [send]
  );

  const addMessage = useCallback(
    async ({
      chat_id,
      message: messageParams,
      statusMessage,
      updateLastRead = true,
    }: AddMessageParams) => {
      const cli_msg_id = genId();

      // Add to State and DB
      const time = serverTime.time;
      const messageParams_ = {
        cli_msg_id,
        create_at: time,
        update_at: time,
        owner_id: user?.userId,
        pulled: ChatMessagePulled.YES,
        ...messageParams,
      };

      const [
        {
          messages: [message],
        },
      ] = await chatMessages.messagesDB.addMessages([
        {
          chat_id,
          messages: [messageParams_],
        },
      ]);

      chatMessages.messagesState.addMessages([
        { chat_id, messages: [Object.assign(message, statusMessage)] },
      ]);

      // Update Chat
      await chats.insertChats([
        Object.assign(
          {
            chat_id,
            update_at: message.create_at,
          },
          updateLastRead
            ? {
                last_read_message_id: message.id,
                last_read_new_message_id: message.id,
              }
            : null
        ),
      ]);

      return message;
    },
    [chatMessages, chats, user?.userId]
  );

  const sendMessageToServer = useCallback(
    async (
      params: SendMessageToServerParams,
      isRetry?: (err: unknown) => ResolveValue<boolean>
    ) => {
      const { sendParams, message, flags, handlers, autoRetry = true } = params;
      const { encrypt, updateMessageBefore } = handlers || {};
      const { chat_id } = sendParams.chat;

      let flags_ = flags;

      return retry(
        async () => {
          let encryptMessage: ChatMessageIdParams | undefined;
          if (
            encrypt &&
            // TODO amo: File msg encrypt handle
            message.payload_type === ChatMessagePayloadType.ENCRYPTED_TEXT
          ) {
            encryptMessage = await encrypt(message);
          }

          chatMessages.messagesState.updateMessages([
            {
              chat_id,
              messages: [
                {
                  id: message.id,
                  status: ChatMessageStatus.LOADING,
                },
              ],
            },
          ]);

          // Send to server
          const res = await sendMessage(
            sendParams.chat,
            {
              cliMsgId: message.cli_msg_id,
              payloadType: message.payload_type,
              expireDuration: message.expire_duration,
              expectReadList: sendParams.message.expect_read_list,
              encryptIndex: encryptMessage
                ? encryptMessage.encrypt_index
                : message.encrypt_index,
              payload: encryptMessage ? encryptMessage.payload : message.payload,
            },
            flags_,
            {
              maxCount: 0,
            }
          ).catch((err) => {
            flags_ = {
              ...flags_,
              retry: FlagValue.YES,
            };
            throw err;
          });

          const message_ = {
            id: message.id,
            status: ChatMessageStatus.SUCCESS,
            message_id: res.body.messageId,
            create_at: res.body.createdAt,
            update_at: res.body.createdAt,
          };

          Object.assign(
            message_,
            await updateMessageBefore?.(message_, res, sendParams)
          );

          await chatMessages.updateMessages([
            {
              chat_id,
              messages: [message_],
            },
          ]);

          return {
            ...message,
            ...message_,
          } as ChatMessage;
        },
        {
          isRetry: (err) => (isRetry ? isRetry(err) : autoRetry),
        }
      ).catch(async (err) => {
        await chatMessages.updateMessages([
          {
            chat_id,
            messages: [
              {
                id: message.id,
                status: ChatMessageStatus.ERROR,
              },
            ],
          },
        ]);

        throw err;
      });
    },
    [chatMessages, sendMessage]
  );

  const uploadFileMessage = useCallback(
    async ({
      chat_id,
      msg,
      file,
      info,
    }: {
      chat_id: ChatChatId;
      msg: ChatMessage;
      file: File;
      info?: FileInfo;
    }) => {
      const {
        chatMessages: { updateMessages },
        fileMessages: { uploadFile },
      } = hooks as ChatContextHooksFull;

      info = await uploadFile({
        id: msg.cli_msg_id,
        file,
        info,
      });

      msg.payload = JSON.stringify(info);

      await updateMessages([
        {
          chat_id,
          messages: [
            {
              id: msg.id,
              payload: msg.payload,
            },
          ],
        },
      ]);

      return msg;
    },
    [hooks]
  );

  const abortFileMessage = useCallback(
    async ({ chat_id, msg }: { chat_id: ChatChatId; msg: ChatMessage }) => {
      const {
        chatMessages: { updateMessages },
        fileMessages: { getFileMessage, abortFile, removeFileMessage },
      } = hooks as ChatContextHooksFull;

      const fm = await getFileMessage(msg.cli_msg_id);

      if (fm) {
        fm.info.status = FileInfoStatus.CANCELED;

        await abortFile(msg.cli_msg_id);

        await removeFileMessage(msg.cli_msg_id);

        await updateMessages([
          {
            chat_id,
            messages: [
              {
                id: msg.id,
                payload: JSON.stringify(fm.info),
              },
            ],
          },
        ]);
      }
    },
    [hooks]
  );

  const sendMessageCommon = useCallback(
    async (sendParams: SendParams, sendToServer: typeof sendMessageToServer) => {
      const {
        chatMessages: { messagesState },
        fileMessages: { genFileInfo },
      } = hooks as ChatContextHooksFull;
      const {
        message: messageParams,
        chat: { chat_id, expire_duration },
      } = sendParams;

      const stateMessageParams: ChatMessagePartial = {};

      const isFS = isFileMessage(messageParams);
      let fileInfo: FileInfo | undefined;

      if (isFS) {
        fileInfo = await genFileInfo(messageParams.data);

        fileInfo.status = FileInfoStatus.ERROR;
        messageParams.payload = JSON.stringify(fileInfo);

        fileInfo.status = undefined;
        stateMessageParams.payload = JSON.stringify(fileInfo);
      }

      let message_ = await addMessage({
        chat_id,
        message: { expire_duration, ...messageParams, status: ChatMessageStatus.ERROR },
        statusMessage: {
          ...stateMessageParams,
          status: ChatMessageStatus.LOADING,
        },
      });

      try {
        if (isFS) {
          message_ = await uploadFileMessage({
            chat_id,
            msg: message_,
            file: messageParams.data,
            info: fileInfo,
          });
        }

        message_ = await sendToServer({
          sendParams,
          message: message_,
        });
      } catch {
        const msg: ChatMessagePartial = {
          id: message_.id,
          status: ChatMessageStatus.ERROR,
        };

        if (fileInfo) {
          fileInfo.status = FileInfoStatus.ERROR;
          msg.payload = JSON.stringify(fileInfo);
        }

        await messagesState.updateMessages([
          {
            chat_id,
            messages: [msg],
          },
        ]);
      }

      // Update Chat
      await chats.insertChats([
        {
          chat_id,
          update_at: message_.create_at,
        },
      ]);
    },
    [addMessage, chats, hooks, uploadFileMessage]
  );

  const retryMessage = useCallback(
    async (retryParams: RetryParams, sendToServer: typeof sendMessageToServer) => {
      const { chat, message } = retryParams;
      if (message.status === ChatMessageStatus.ERROR) {
        if (isFileMessage(message)) {
          const info = safeJson(message.payload, {} as any);
          if (!info.meta) {
            return;
          }
        }

        const params: SendMessageToServerParams = {
          sendParams: {
            chat,
            message,
          },
          message,
          flags: {
            retry: FlagValue.YES,
          },
          autoRetry: false,
        };
        await sendToServer(params);
      }
    },
    []
  );

  const updateLastReadMessages = useCallback<
    <T extends ChatArgType<U>, U extends ChatMessagePartial = ChatMessagePartial>(
      paramsList: T[]
    ) => Promise<void>
  >(
    async (paramsList) => {
      // Find the latest read message
      const readMessageMap: Record<
        ChatChatId,
        { chat: Chat; msg: ChatMessagePartial | undefined }
      > = {};
      await Promise.all(
        paramsList.map(async ({ chat_id, messages }) => {
          const chat = await chats.getChat({ chat_id });
          if (chat) {
            let item = readMessageMap[chat_id];
            if (!item) {
              item = readMessageMap[chat_id] = { chat, msg: undefined };
            }
            messages.forEach((msg) => {
              if (
                msg.category === ChatMessageCategory.SEND ||
                msg.status === ChatMessageStatus.READ ||
                msg.status === ChatMessageStatus.PARTIAL_READ
              ) {
                if (!item.msg || msg.update_at! >= item.msg.update_at!) {
                  item.msg = msg;
                }
              }
            });
          }
        })
      );

      // Update the last read message
      const chatParamsList: Array<{
        chat: Chat;
        message: ChatMessage;
      }> = [];

      await Promise.all(
        Object.entries(readMessageMap).map(async ([, { chat, msg }]) => {
          if (msg) {
            const message = await chatMessages.getMessage(chat.chat_id, msg);
            if (message) {
              chatParamsList.push({
                chat,
                message,
              });
            }
          }
        })
      );

      await chats.updateLastReadMessages(chatParamsList);
    },
    [chatMessages, chats]
  );

  const updateStateMessageAsRead = useCallback(
    async (chat: Chat) => {
      if (chat.last_read_message_id !== chat.last_read_new_message_id) {
        await chats.chatsState.insertChats([
          {
            chat_id: chat.chat_id,
            last_read_new_message_id: chat.last_read_message_id,
          },
        ]);
      }
    },
    [chats]
  );

  const pullMessagesCommon = useCallback<typeof pullMessages>(
    async (type, params, handlers) => {
      const {
        chatMediaSingleValue: { mediaNoticeSingle },
        chatMediaGroupValue: { mediaNoticeGroup, mediaStatusUpdateGroup },
      } = hooks as ChatContextHooksFull;

      const result = await pullMessages(type, params, handlers);
      if (result.pulledListMediaSender.length) {
        // TODO amo: Multiple device
        await mediaNoticeSingle(result.pulledListMediaSender);
        await mediaNoticeGroup(result.pulledListMediaReceiver);
        await mediaStatusUpdateGroup(result.pulledList);
      }
      if (result.pulledListMediaReceiver.length) {
        await mediaNoticeSingle(result.pulledListMediaReceiver);
        await mediaNoticeGroup(result.pulledListMediaReceiver);
        await mediaStatusUpdateGroup(result.pulledList);
      }
      if (result.deletedList.length) {
        await chatMessages.removeMessages(
          await mapMessagesArgs(result.deletedList, (item) => item.id!)
        );
      }
      return result;
    },
    [hooks, pullMessages, chatMessages]
  );

  const pullCommon = useCallback(
    async (
      type: ChatCategory,
      serverSequence: number,
      handlers: {
        loadChats: (
          params: Parameters<UseChatsValueResult['loadChatByChatId']>[0]
        ) => ReturnType<UseChatsValueResult['loadChatByChatId']>;
        pullMessages?: (
          list: ChatMessagesArgMessageIdPullParams[]
        ) => ResolveValue<ReturnType<typeof pullMessages>>;
        pullMessagesBefore?: (
          messages: ChatMessageMessageIdParams[],
          chat_id: ChatChatId
        ) => ResolveValue<ChatMessageMessageIdParams[]>;
        updateMessagesBefore?: (
          updateList: ChatMessagesArgMessageIdParams[]
        ) => ResolveValue<DBChatMessagesArgPartial[]>;
        systemMessagesHandler?: (
          result: Awaited<ReturnType<typeof pullMessages>>
        ) => ResolveValue<void>;
      }
    ) => {
      const { chatMediaSingleValue } = hooks as ChatContextHooksFull;
      const pullResult = await pullMessageIds(type, serverSequence);
      if (pullResult) {
        const {
          loadChats,
          pullMessages,
          pullMessagesBefore,
          updateMessagesBefore,
          systemMessagesHandler,
        } = Object.assign({}, handlers);
        const {
          addList,
          addListSender,
          addListMedia,
          updateList,
          updateListMedia,
          systemList,
          systemEncryptionList,
          saveSequence,
          body,
        } = pullResult;

        if (systemEncryptionList.length) {
          await pullMessages?.(systemEncryptionList);
        }

        // TODO: check mute chat
        const isMuteChat = async (_chatId: ChatChatId) => {
          return new Promise((resolve) => {
            const timeout = setTimeout(() => {
              resolve(false);
              clearTimeout(timeout);
            }, 20);
          });
        };

        if (addList.length) {
          if (addList.length !== addListSender.length) {
            const notSenderList = addList.filter(
              (item) => !Object.keys(addListSender).includes(item.chat_id)
            );
            const canPlayAudio = notSenderList.every(
              async (item) => await isMuteChat(item.chat_id)
            );
            if (canPlayAudio) {
              playReceiveSound();
            }
          }
          // Handle chats
          let addList_: Array<ChatMessagesArgMessageIdPullParams> = [];
          await Promise.all(
            addList.map(async (params) => {
              const { chat, isNew } = await loadChats(params);
              if (chat) {
                addList_.push(Object.assign(params, { isNew }));
              }
            })
          );

          addList_ = (await chatMessages.messagesDB.insertMessages(
            addList_
          )) as Array<ChatMessagesArgMessageIdPullParams>;

          if (addListSender.length) {
            // Update for self message
            await updateLastReadMessages(addListSender);
          }

          if (addListMedia.length) {
            // Update message_id by cli_msg_id
            chatMediaSingleValue.mediaStatusUpdateSingle(addListMedia);
          }

          // Pull important messages
          const pullMessagesArg = (
            await Promise.all(
              addList_.map(async ({ chat_id, messages, ...other }) => {
                let messages_: ChatMessageMessageIdParams[];

                if (chat_id === refs.current.activatedChatId) {
                  messages_ = messages;
                } else {
                  messages_ = messages.splice(-1);

                  const commonMsgs = messages.filter((msg) => {
                    if (chatMediaCall.isMediaPayloadTypeCall(msg.payload_type!)) {
                      messages_.push(msg);
                    } else {
                      return true;
                    }
                  }, []);

                  if (commonMsgs.length && pullMessagesBefore) {
                    messages_.push(...(await pullMessagesBefore(commonMsgs, chat_id)));
                  }
                }

                if (messages_.length) {
                  return { ...other, chat_id, messages: messages_ };
                }
              })
            )
          ).filter((item) => item !== undefined);

          const result = await pullMessages?.(pullMessagesArg);

          if (result) {
            const { list } = result;

            // Update activated chat messages
            await chatMessages.messagesDB.updateMessages(list);

            for (const args of list) {
              if (args.chat_id === refs.current.activatedChatId) {
                chatMessages.messagesState.insertMessages([args]);
                break;
              }
            }

            // Update chat last message
            const chatArgs_ = list
              .map(({ chat_id, messages }) => {
                const latestMessage = messages.at(-1);
                if (latestMessage) {
                  return {
                    chat_id,
                    update_at: latestMessage.create_at,
                  };
                }
              })
              .filter(Boolean) as ChatParams[];

            await chats.insertChats(chatArgs_);
          }
        }

        if (updateList.length) {
          await chatMessages.updateMessages(
            updateMessagesBefore ? await updateMessagesBefore(updateList) : updateList
          );
        }

        if (updateListMedia.length) {
          chatMediaSingleValue.mediaStatusUpdateSingle(updateListMedia);
        }

        if (systemList.length) {
          const result = await pullMessages?.(systemList);
          if (result) {
            await systemMessagesHandler?.(result);
          }
        }

        await saveSequence();

        if (body.messageSequenceList.length) {
          await pullCommon(type, Infinity, handlers);
        }
      }
    },
    [
      hooks,
      pullMessageIds,
      chatMessages,
      playReceiveSound,
      updateLastReadMessages,
      refs,
      chatMediaCall,
      chats,
    ]
  );

  const loadMessages = useCallback(
    async (
      params: ChatMessagesLoadParams,
      pullMessages: (
        args: ChatMessagesArgMessageIdParams
      ) => ResolveValue<ChatMessagesArgMessageIdParams>
    ) => {
      const messagesIdArg = await chatMessages.loadMessages(params);
      (async () => {
        try {
          const messagesArg = await pullMessages(messagesIdArg);
          chatMessages.updateMessages([messagesArg]);
        } catch (_err) {
          const errorMsgs = await mapMessagesArgs([messagesIdArg], (msg) => ({
            id: msg.id,
            pulled: ChatMessagePulled.ERROR,
          }));
          chatMessages.messagesState.updateMessages(errorMsgs);
        }
      })();
      return messagesIdArg;
    },
    [chatMessages]
  );

  const getUnreadMessageCount = useCallback(
    async (chatId: ChatChatId, last_read_msg_id: number) => {
      const count = await chatMessages.getUnreadMessageCount(chatId, last_read_msg_id);
      return count;
    },
    [chatMessages]
  );

  const backChatUpdateAt = useCallback(
    async (query: ChatQuery) => {
      const chat = await chats.getChat(query);
      if (chat) {
        chatMessages.getLastMessage(chat.chat_id).then((res) => {
          chats.insertChats([
            { chat_id: chat.chat_id, update_at: res?.create_at || chat.create_at },
          ]);
        });
      }
    },
    [chatMessages, chats]
  );

  const deleteExpiredMessages = useCallback(
    async (id: ChatChatId, timestamp: number, messageIds: ChatMessageId[]) => {
      await chatMessages.deleteExpiredMessages(id, timestamp, messageIds);
      backChatUpdateAt({ chat_id: id });
    },
    [chatMessages, backChatUpdateAt]
  );

  const deleteExpiredMessagesByIds = useCallback(
    async (chatId: ChatChatId, messageIds: ChatMessageId[]) => {
      await chatMessages.deleteExpiredMessagesByIds(chatId, messageIds);
      backChatUpdateAt({ chat_id: chatId });
    },
    [chatMessages, backChatUpdateAt]
  );

  const readQueue = useQueue();

  const markMessagesAsRead = useCallback(
    (chat: Chat, messages: ChatMessage[]) => {
      return readQueue.push(async () => {
        const unreadMessages_: ChatMessage[] = [];

        await Promise.all(
          messages.map(async (message) => {
            const message_ = await chatMessages.getMessage(chat.chat_id, message);
            if (message_) {
              if (
                message_.status === ChatMessageStatus.UNREAD &&
                message_.category === ChatMessageCategory.RECEIVE
              ) {
                unreadMessages_.push(message_);
              }
            }
          })
        );
        unreadMessages_.sort((a, b) => a.id - b.id);

        if (unreadMessages_.length) {
          const res = await messageReadReply({
            chatId: chat.chat_id,
            messageIdList:
              chat.category === ChatCategory.GROUP
                ? unreadMessages_.map(
                    (message: ChatMessage) =>
                      `${message.message_id}_${message.cli_msg_id}_${message.owner_id}`
                  )
                : unreadMessages_.map((message: ChatMessage) => message.message_id),
          });

          await chatMessages.updateMessages([
            {
              chat_id: chat.chat_id,
              messages: unreadMessages_.map((message: ChatMessage) => ({
                message_id: message.message_id,
                status: ChatMessageStatus.READ,
                read_at: res.createdAt,
              })),
            },
          ]);
        }

        const lasMessage = messages.sort((a, b) => a.id - b.id).at(-1)!;
        await chats.updateLastReadMessages([{ chat, message: lasMessage }]);
      });
    },
    [messageReadReply, chatMessages, chats, readQueue]
  );

  return useMemo(
    () => ({
      activatedMessages,
      sendMessageCommon,
      pullMessagesCommon,
      pullCommon,
      addMessage,
      retryMessage,
      loadMessages,
      getUnreadMessageCount,
      markMessagesAsRead,
      deleteExpiredMessages,
      deleteExpiredMessagesByIds,
      updateStateMessageAsRead,
      sendMessageToServer,
      updateLastReadMessages,
      messageArrivalReply,
      abortFileMessage,
    }),
    [
      activatedMessages,
      sendMessageCommon,
      pullMessagesCommon,
      pullCommon,
      addMessage,
      retryMessage,
      loadMessages,
      getUnreadMessageCount,
      markMessagesAsRead,
      deleteExpiredMessages,
      deleteExpiredMessagesByIds,
      updateStateMessageAsRead,
      sendMessageToServer,
      updateLastReadMessages,
      messageArrivalReply,
      abortFileMessage,
    ]
  );
}
